// Renders all overlay objects onto an existing Fabric canvas.

import { calculatePolygonArea, calculatePolygonPerimeter } from "./geometry";
import { computeDistance } from "./interactions/handlersMouseDown";
import { getLineGuidelineIntersection } from "./intersections";

// --- transient highlight state (module-level) ---
let _highlightedPolygonId = null;
export const setHighlightedPolygonId = (idOrNull) => {
  _highlightedPolygonId = idOrNull || null;
};

function resolveImageSrc(mod) {
  // Accepts: string, {default: string}, {src: string}
  if (!mod) return "";
  if (typeof mod === "string") return mod;
  if (typeof mod.default === "string") return mod.default;
  if (typeof mod.src === "string") return mod.src;
  return "";
}
const COMMENT_ICON_MOD = require("../../assets/image-analysis/comentIcon.png");
const COMMENT_ICON_SRC = resolveImageSrc(COMMENT_ICON_MOD);

function safeText(value) {
  if (value === null || value === undefined) return "";
  if (typeof value !== "string") return String(value);
  return value;
}

function dist(p, q) {
  return Math.hypot((p?.x ?? 0) - (q?.x ?? 0), (p?.y ?? 0) - (q?.y ?? 0));
}

function distanceAndProjection(P, A, B) {
  const vx = (B?.x ?? 0) - (A?.x ?? 0);
  const vy = (B?.y ?? 0) - (A?.y ?? 0);
  const wx = (P?.x ?? 0) - (A?.x ?? 0);
  const wy = (P?.y ?? 0) - (A?.y ?? 0);
  const len2 = vx * vx + vy * vy;
  let t = len2 ? (vx * wx + vy * wy) / len2 : 0;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  const proj = { x: (A?.x ?? 0) + t * vx, y: (A?.y ?? 0) + t * vy };
  const d = Math.hypot((P?.x ?? 0) - proj.x, (P?.y ?? 0) - proj.y);
  return { d, t };
}

// IMPORTANT: does NOT touch backgroundImage.
export const renderFabricScene = ({
  fabricCanvas,
  fabric,
  measurements,
  guidelines,
  polygons,
  comments,
  textAnnotations,
  currentPoints,
  hoveredPoint,
  selectedMeasurement,
  selectedGuideline,
  selectedPolygon,
  selectedCommentId,
  selectedText,
  styleSettings,
  activeTool,
  canvasSize,
  unit,
  scale,
  scalePosition,
  scaleLength,
  scaleAngle,
  // getLineGuidelineIntersection,
  isPointNearPoint,
  dots,
  selectedDot,
  arrows,
  selectedArrowId,
}) => {
  // Remove ONLY overlay objects, keep bg intact
  const objs = fabricCanvas.getObjects();
  objs.forEach((obj) => fabricCanvas.remove(obj));

  for (const g of guidelines) {
    const isSelected = selectedGuideline === g.id;
    const line = new fabric.Line(
      g.type === "vertical"
        ? [g.position, 0, g.position, canvasSize.height]
        : [0, g.position, canvasSize.width, g.position],
      {
        stroke: isSelected ? styleSettings.unitColor : "#8B5CF6",
        strokeWidth: 2,
        selectable: activeTool === "select",
        evented: activeTool === "select",
        hasControls: false,
        data: { type: "guideline", id: g.id },
        perPixelTargetFind: false,
      }
    );

    fabricCanvas.add(line);

    // Fabric v6 z-order: send to bottom
    try {
      fabricCanvas.moveTo(line, 0);
    } catch {}
  }

  // --- Dots

  for (const d of dots) {
    const r = Number(d.radius ?? 5);
    const isSelected = selectedDot === d.id;

    // zoom-aware, subpixel center snap threshold
    const zoom =
      typeof fabricCanvas.getZoom === "function" ? fabricCanvas.getZoom() : 1;
    // ~10% of radius, scaled by zoom, clamped to ~0.35–0.8 screen px
    const CENTER_EPS = Math.max(0.35, Math.min(0.8, (r * 0.1) / zoom));

    // crossing threshold: allow the shaft to pass through dot center area
    const CROSS_EPS = Math.max(0.5, r - 1); // unchanged
    let isTouching = false;

    // 1) endpoint MUST be (almost) exactly at the dot center
    for (const m of measurements) {
      if ("start" in m && "end" in m) {
        const dStart = dist(d.position, m.start);
        const dEnd = dist(d.position, m.end);
        if (dStart <= CENTER_EPS || dEnd <= CENTER_EPS) {
          isTouching = true;
          break;
        }
      }
    }

    // 2) line crosses the dot (interior-only projection)
    if (!isTouching) {
      for (const m of measurements) {
        if ("start" in m && "end" in m) {
          const { d: segDist, t } = distanceAndProjection(
            d.position,
            m.start,
            m.end
          );
          if (segDist <= CROSS_EPS && t > 0.05 && t < 0.95) {
            isTouching = true;
            break;
          }
        }
      }
    }
    // 3) ANGLE endpoints (center, point1, point2) exactly at dot center  🔴 NEW
    if (!isTouching) {
      for (const m of measurements) {
        if ("center" in m && "point1" in m && "point2" in m) {
          // treat these as the three draggable endpoint circles
          const dc = dist(d.position, m.center);
          const d1 = dist(d.position, m.point1);
          const d2 = dist(d.position, m.point2);
          if (dc <= CENTER_EPS || d1 <= CENTER_EPS || d2 <= CENTER_EPS) {
            isTouching = true;
            break;
          }
        }
      }
    }

    // 4) angle arms (keep existing interior rule)
    if (!isTouching) {
      for (const m of measurements) {
        if ("center" in m && "point1" in m && "point2" in m) {
          const a = distanceAndProjection(d.position, m.center, m.point1);
          const b = distanceAndProjection(d.position, m.center, m.point2);
          if (
            (a.d <= CROSS_EPS && a.t > 0.05 && a.t < 0.95) ||
            (b.d <= CROSS_EPS && b.t > 0.05 && b.t < 0.95)
          ) {
            isTouching = true;
            break;
          }
        }
      }
    }

    const fillColor = isTouching ? "#FF0000" : d.color ?? "#FF0000";

    const dot = new fabric.Circle({
      left: (d.position?.x ?? 0) - r,
      top: (d.position?.y ?? 0) - r,
      radius: r,
      fill: isSelected ? styleSettings.unitColor : fillColor,
      selectable: false,
      evented: activeTool === "select",
      hasControls: false,
      data: { type: "dot", id: d.id },
    });
    fabricCanvas.add(dot);

    if (isSelected) {
      const ring = new fabric.Circle({
        left: (d.position?.x ?? 0) - (r + 3),
        top: (d.position?.y ?? 0) - (r + 3),
        radius: r + 3,
        fill: "transparent",
        stroke: "#f59e0b",
        strokeWidth: 2,
        selectable: false,
        evented: false,
        data: { type: "dot-selection", id: d.id },
      });
      fabricCanvas.add(ring);
    }
  }

  // arrow

  // --- Arrows (start circle + end arrowhead) ---
  for (const a of arrows || []) {
    // const strokeColor = "#ff0008ff";
    const isSelected = selectedArrowId === a.id;
    const strokeColor = isSelected ? styleSettings.unitColor : "#ff0008ff";
    // shaft
    const shaft = new fabric.Line([a.start.x, a.start.y, a.end.x, a.end.y], {
      stroke: strokeColor,

      strokeWidth: 2,
      selectable: false,
      evented: activeTool === "select",
      data: { type: "arrow", id: a.id, part: "body" },
    });
    fabricCanvas.add(shaft);

    // start marker (circle)
    const startCircle = new fabric.Circle({
      left: a.start.x - 6,
      top: a.start.y - 6,
      radius: 6,
      fill: strokeColor,
      selectable: false,
      evented: activeTool === "select",
      data: { type: "arrow", id: a.id, part: "start" },
    });
    fabricCanvas.add(startCircle);

    // end arrowhead (triangle)
    const dx = a.end.x - a.start.x;
    const dy = a.end.y - a.start.y;
    const angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;

    const head = new fabric.Triangle({
      left: a.end.x,
      top: a.end.y,
      width: 12,
      height: 12,
      angle: angleDeg + 90, // fabric triangle points up; rotate to face line
      originX: "center",
      originY: "center",
      fill: strokeColor,
      selectable: false,
      evented: activeTool === "select",
      data: { type: "arrow", id: a.id, part: "end" },
    });
    fabricCanvas.add(head);
  }

  // --- Text annotations
  for (const annotation of textAnnotations) {
    if (annotation.isEditing) continue; // textarea overlay handles editing
    const isSelected = selectedText === annotation.id;
    // 1) Measure the text so the box fits its content
    //    (use fabric.Text for an accurate unwrapped width)

    const measure = new fabric.Text(safeText(annotation.text) || "", {
      fontSize: 14,
      fontFamily: "Inter, sans-serif",
    });
    // keep a little padding so the text isn't flush to the edge
    const PADDING = 8;
    const contentWidth = Math.ceil(measure.width || 0);
    const autoWidth = Math.max(1, contentWidth + PADDING * 2);
    // If you always want "fit-to-text", just use autoWidth:
    const width = autoWidth;
    const textbox = new fabric.Textbox(safeText(annotation.text), {
      left: annotation.position.x,
      top: annotation.position.y,
      width: width,
      height: annotation.height,
      hasBorders: true,
      fontSize: 14,
      fontFamily: "Inter, sans-serif",
      fill: isSelected ? styleSettings.unitColor : "#FFD700",
      backgroundColor: "transparent",
      angle: annotation.rotation,
      selectable: false,
      evented: activeTool === "select",
      splitByGrapheme: false,
      textAlign: "left",
      lineHeight: 1.3,
      data: { type: "text-annotation", id: annotation.id },
      borderColor: isSelected ? "#f59e0b" : "#3b82f6",
      cornerColor: isSelected ? "#f59e0b" : "#3b82f6",
      cornerSize: 8,
      transparentCorners: false,
      hasControls: isSelected && activeTool === "select",
      hasBorders: isSelected && activeTool === "select",
      padding: PADDING,
      lockMovementX: true,
      lockMovementY: true,
      hoverCursor: "move",
    });
    if (isSelected && activeTool === "select") {
      textbox.setControlsVisibility({
        mt: true,
        mb: true,
        ml: true,
        mr: true,
        tl: true,
        tr: true,
        bl: true,
        br: true,
        mtr: true,
      });
    }
    fabricCanvas.add(textbox);
  }
  // --- Scale ruler (with perpendicular end markers that touch the line) ---
  const start = scalePosition;
  const end = {
    x: scalePosition.x + scaleLength * Math.cos(scaleAngle),
    y: scalePosition.y + scaleLength * Math.sin(scaleAngle),
  };
  const scaleSelected = selectedMeasurement === "scale";

  const COLOR = scaleSelected ? "#f59e0b" : "#AAFF00";
  const STROKE = 2;
  const MARK_LEN = 20; // total length of horizontal markers

  // Unit direction vector of the main line
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const len = Math.max(1e-6, Math.hypot(dx, dy));
  const ux = dx / len;
  const uy = dy / len;

  // Perpendicular vector (for the small T-mark)
  const px = -uy;
  const py = ux;

  // Main measurement line
  fabricCanvas.add(
    new fabric.Line([start.x, start.y, end.x, end.y], {
      stroke: COLOR,
      strokeWidth: STROKE,
      selectable: false,
      evented: activeTool === "select",
      data: { type: "scale-line", id: "scale" },
    })
  );

  // Helper to add perpendicular line marker that “touches” the main line
  const addTouchingMarker = (center) => {
    const x1 = center.x + px * (MARK_LEN / 2);
    const y1 = center.y + py * (MARK_LEN / 2);
    const x2 = center.x - px * (MARK_LEN / 2);
    const y2 = center.y - py * (MARK_LEN / 2);
    fabricCanvas.add(
      new fabric.Line([x1, y1, x2, y2], {
        stroke: COLOR,
        strokeWidth: STROKE,
        selectable: false,
        evented: false,
        data: { type: "scale-marker" },
      })
    );
  };

  // Draw end markers directly at start and end points (they touch the line)
  addTouchingMarker(start);
  addTouchingMarker(end);

  // Measurement label slightly offset near the top marker
  const updatedScale = scale > 0 ? scale : scaleLength.toFixed(0);
  fabricCanvas.add(
    new fabric.Text(`${updatedScale} ${unit}`, {
      left: start.x + 8,
      top: start.y + 8,
      fontSize: 12,
      fontFamily: "Inter, sans-serif",
      fontWeight: "bold",
      fill: COLOR,
      selectable: false,
      evented: false,
      data: { type: "scale-label" },
    })
  );

  // // --- Scale ruler (inward arrows that touch the line) ---

  // --- Measurements
  for (const m of measurements) {
    if ("start" in m) {
      const isSelected = selectedMeasurement === m.id;
      // pick the effective style: per-object > global setting
      const effectiveLineStyle =
        m.lineStyle ?? styleSettings.linear.dashStyle ?? "solid";

      // map style -> dash array
      const dashFor = (style) =>
        style === "dashed" ? [5, 5] : style === "dotted" ? [2, 2] : undefined; // solid

      for (const g of guidelines) {
        const inter = getLineGuidelineIntersection(m.start, m.end, g);
        if (inter) {
          const dot = new fabric.Circle({
            left: inter.x - 5,
            top: inter.y - 5,
            radius: 5,
            fill: "#FF0000",
            selectable: false,
            evented: false,
            data: { type: "intersection-dot" },
          });
          fabricCanvas.add(dot);
        }
      }
      const line = new fabric.Line([m.start.x, m.start.y, m.end.x, m.end.y], {
        stroke: isSelected
          ? styleSettings.unitColor
          : m.color ?? styleSettings.linear.color,

        strokeWidth: m.thickness ?? styleSettings.linear.thickness,
        strokeDashArray: dashFor(effectiveLineStyle),
        selectable: false,
        evented: activeTool === "select",
        data: {
          type: "measurement",
          id: m.id,
          measurementType: "linear",
        },
      });
      fabricCanvas.add(line);

      const s = new fabric.Circle({
        left: m.start.x - (isSelected ? 4 : 4),
        top: m.start.y - (isSelected ? 4 : 4),
        radius: isSelected ? 4 : 4,
        fill: isSelected
          ? styleSettings.unitColor
          : m.color ?? styleSettings.linear.color,
        selectable: false,
        evented: activeTool === "select",
        data: {
          type: "measurement-point",
          measurementId: m.id,
          pointType: "start",
        },
      });
      fabricCanvas.add(s);

      const e = new fabric.Circle({
        left: m.end.x - (isSelected ? 4 : 4),
        top: m.end.y - (isSelected ? 4 : 4),
        radius: isSelected ? 4 : 4,
        fill: isSelected
          ? styleSettings.unitColor
          : m.color ?? styleSettings.linear.color,
        selectable: false,
        evented: activeTool === "select",
        data: {
          type: "measurement-point",
          measurementId: m.id,
          pointType: "end",
        },
      });
      fabricCanvas.add(e);

      const midX = (m.start.x + m.end.x) / 2;
      const midY = (m.start.y + m.end.y) / 2;
      const freshDistance = computeDistance(m.start, m.end, {
        scale,
        scaleLength,
      });

      // direction of the line
      const dx = m.end.x - m.start.x;
      const dy = m.end.y - m.start.y;
      const len = Math.hypot(dx, dy) || 1;

      const ux = dx / len;
      const uy = dy / len;

      // perpendicular unit (two possible sides)
      let px = -uy;
      let py = ux;

      // pick the more stable side:
      // horizontal line => push up
      // vertical line   => push right
      // diagonal        => push up
      if (Math.abs(uy) < 0.25) {
        // horizontal-ish
        if (py > 0) {
          px = -px;
          py = -py;
        }
      } else if (Math.abs(ux) < 0.25) {
        // vertical-ish
        if (px < 0) {
          px = -px;
          py = -py;
        }
      } else {
        // diagonal
        if (py > 0) {
          px = -px;
          py = -py;
        }
      }

      const label = new fabric.Text(`${freshDistance.toFixed(2)} ${unit}`, {
        left: 0,
        top: 0,
        originX: "center",
        originY: "center",
        fontSize: 12,
        fontFamily: "Inter, sans-serif",
        fill: isSelected
          ? styleSettings.unitColor
          : m.color ?? styleSettings.linear.color,
        selectable: false,
        evented: false,
        data: { type: "measurement-label", measurementId: m.id },
      });
      // --- guaranteed clearance but still close ---
      const halfProj =
        Math.abs(px) * (label.width / 2) + Math.abs(py) * (label.height / 2);

      const strokeSafe = (styleSettings?.linear?.width || 2) / 2;
      const PADDING = 3; // close but safe
      const BASE = 6; // small lift off the line
      const offset = BASE + halfProj + strokeSafe + PADDING;
      label.set({
        left: midX + px * offset,
        top: midY + py * offset,
      });
      fabricCanvas.add(label);
    } else {
      const a = m;
      const isSelected = selectedMeasurement === m.id;

      // --- intersections of angle arms with guidelines
      for (const g of guidelines) {
        // arm1: center -> point1
        const inter1 = getLineGuidelineIntersection(a.center, a.point1, g);
        if (inter1) {
          fabricCanvas.add(
            new fabric.Circle({
              left: inter1.x - 5,
              top: inter1.y - 5,
              radius: 5,
              fill: "#FF0000",
              selectable: false,
              evented: false,
              data: {
                type: "intersection-dot",
                measurementId: a.id,
                arm: "arm1",
              },
            })
          );
        }
        // arm2: center -> point2
        const inter2 = getLineGuidelineIntersection(a.center, a.point2, g);
        if (inter2) {
          fabricCanvas.add(
            new fabric.Circle({
              left: inter2.x - 5,
              top: inter2.y - 5,
              radius: 5,
              fill: "#FF0000",
              selectable: false,
              evented: false,
              data: {
                type: "intersection-dot",
                measurementId: a.id,
                arm: "arm2",
              },
            })
          );
        }
      }

      const arm1 = new fabric.Line(
        [a.center.x, a.center.y, a.point1.x, a.point1.y],
        {
          stroke: isSelected
            ? styleSettings.unitColor
            : m.color ?? styleSettings.angle.color,

          strokeWidth: m.thickness ?? styleSettings.angle.thickness,
          selectable: false,
          evented: activeTool === "select",
          hasControls: false,
          data: {
            type: "measurement",
            id: m.id,
            measurementType: "angle",
            part: "arm1",
          },
        }
      );
      fabricCanvas.add(arm1);

      const arm2 = new fabric.Line(
        [a.center.x, a.center.y, a.point2.x, a.point2.y],
        {
          stroke: isSelected
            ? styleSettings.unitColor
            : m.color ?? styleSettings.angle.color,

          strokeWidth: m.thickness ?? styleSettings.angle.thickness,
          selectable: false,
          evented: activeTool === "select",
          hasControls: false,
          data: {
            type: "measurement",
            id: m.id,
            measurementType: "angle",
            part: "arm2",
          },
        }
      );
      fabricCanvas.add(arm2);

      // --- always-visible circular arc (ring) around the vertex

      const cx = a.center.x;
      const cy = a.center.y;
      const r = 15; // circle radius; tweak as you like

      // optional soft halo for visibility
      const halo = new fabric.Circle({
        left: cx - (r + 2),
        top: cy - (r + 2),
        radius: r + 2,
        fill: "transparent",
        // stroke: "rgba(0,0,0,0.35)", // subtle outline
        strokeWidth: 3,

        selectable: false,
        evented: false,
        data: { type: "angle-arc-halo", measurementId: m.id },
      });
      fabricCanvas.add(halo);

      // main ring
      const ring = new fabric.Circle({
        left: cx - r,
        top: cy - r,
        radius: r,
        fill: "transparent",
        stroke: isSelected
          ? styleSettings.unitColor
          : m.color ?? styleSettings.angle.color,

        strokeWidth: m.thickness ?? styleSettings.angle.thickness,
        selectable: false,
        evented: false,
        data: { type: "angle-arc", measurementId: m.id },
      });
      fabricCanvas.add(ring);

      const c = new fabric.Circle({
        left: a.center.x - (isSelected ? 4 : 4),
        top: a.center.y - (isSelected ? 4 : 4),
        radius: isSelected ? 4 : 4,
        fill: isSelected
          ? styleSettings.unitColor
          : m.color ?? styleSettings.angle.color,
        selectable: false,
        evented: activeTool === "select",
        hasControls: false,
        data: {
          type: "measurement-point",
          measurementId: m.id,
          pointType: "center",
        },
      });
      fabricCanvas.add(c);

      const p1 = new fabric.Circle({
        left: a.point1.x - (isSelected ? 4 : 4),
        top: a.point1.y - (isSelected ? 4 : 4),
        radius: isSelected ? 4 : 4,
        fill: isSelected
          ? styleSettings.unitColor
          : m.color ?? styleSettings.angle.color,
        selectable: false,
        evented: activeTool === "select",
        hasControls: false,
        data: {
          type: "measurement-point",
          measurementId: m.id,
          pointType: "point1",
        },
      });
      fabricCanvas.add(p1);

      const p2 = new fabric.Circle({
        left: a.point2.x - (isSelected ? 4 : 4),
        top: a.point2.y - (isSelected ? 4 : 4),
        radius: isSelected ? 4 : 4,
        fill: isSelected
          ? styleSettings.unitColor
          : m.color ?? styleSettings.angle.color,
        selectable: false,
        evented: activeTool === "select",
        hasControls: false,
        data: {
          type: "measurement-point",
          measurementId: m.id,
          pointType: "point2",
        },
      });
      fabricCanvas.add(p2);

      // --- extra labels: distances from center along each arm
      const d1 = computeDistance(a.center, a.point1, { scale, scaleLength });
      const d2 = computeDistance(a.center, a.point2, { scale, scaleLength });

      // helper: nice label position for an arm
      const computeArmLabelPos = (center, point, sideSign = 1) => {
        const midX = (center.x + point.x) / 2;
        const midY = (center.y + point.y) / 2;

        const dx = point.x - center.x;
        const dy = point.y - center.y;
        const len = Math.hypot(dx, dy) || 1;

        const ux = dx / len; // along arm
        const uy = dy / len;
        const px = -uy * sideSign; // perpendicular
        const py = ux * sideSign;

        const PERP_OFFSET = 16; // how far away from the line
        const ALONG_OFFSET = 4; // a bit away from the center

        return {
          x: midX + px * PERP_OFFSET + ux * ALONG_OFFSET,
          y: midY + py * PERP_OFFSET + uy * ALONG_OFFSET,
        };
      };

      const arm1Pos = computeArmLabelPos(a.center, a.point1, 1);
      let arm2Pos = computeArmLabelPos(a.center, a.point2, 1);

      // if D1/D2 labels are too close, flip D2 to the other side
      const distBetweenLabels = Math.hypot(
        arm2Pos.x - arm1Pos.x,
        arm2Pos.y - arm1Pos.y
      );
      if (distBetweenLabels < 40) {
        const alt = computeArmLabelPos(a.center, a.point2, -1);
        const altDist = Math.hypot(alt.x - arm1Pos.x, alt.y - arm1Pos.y);
        if (altDist > distBetweenLabels) {
          arm2Pos = alt;
        }
      }

      const arm1Label = new fabric.Text(`D1 = ${d1.toFixed(2)} ${unit}`, {
        left: arm1Pos.x,
        top: arm1Pos.y,
        fontSize: 12,
        fontFamily: "Inter, sans-serif",
        fill: isSelected
          ? styleSettings.unitColor
          : m.color ?? styleSettings.angle.color,

        selectable: false,
        evented: false,
        data: {
          type: "measurement-label",
          measurementId: m.id,
          part: "arm1-distance",
        },
      });
      fabricCanvas.add(arm1Label);

      const arm2Label = new fabric.Text(`D2 = ${d2.toFixed(2)} ${unit}`, {
        left: arm2Pos.x,
        top: arm2Pos.y,
        fontSize: 12,
        fontFamily: "Inter, sans-serif",
        fill: isSelected
          ? styleSettings.unitColor
          : m.color ?? styleSettings.angle.color,
        selectable: false,
        evented: false,
        data: {
          type: "measurement-label",
          measurementId: m.id,
          part: "arm2-distance",
        },
      });
      fabricCanvas.add(arm2Label);

      // --- dynamic angle label on the angle bisector ---
      let v1x = a.point1.x - a.center.x;
      let v1y = a.point1.y - a.center.y;
      let v2x = a.point2.x - a.center.x;
      let v2y = a.point2.y - a.center.y;

      const len1 = Math.hypot(v1x, v1y) || 1;
      const len2 = Math.hypot(v2x, v2y) || 1;

      v1x /= len1;
      v1y /= len1;
      v2x /= len2;
      v2y /= len2;

      // bisector direction
      let bx = v1x + v2x;
      let by = v1y + v2y;
      let bLen = Math.hypot(bx, by);

      // if arms are almost opposite, fall back to a perpendicular
      if (bLen < 0.0001) {
        bx = -v1y;
        by = v1x;
        bLen = Math.hypot(bx, by) || 1;
      }

      bx /= bLen;
      by /= bLen;

      const ANGLE_LABEL_OFFSET = r + 20; // r is your existing ring radius
      const angleLabelX = a.center.x + bx * ANGLE_LABEL_OFFSET;
      const angleLabelY = a.center.y + by * ANGLE_LABEL_OFFSET;

      const angleLabel = new fabric.Text(`${a.angle.toFixed(1)}°`, {
        left: angleLabelX,
        top: angleLabelY,
        fontSize: 12,
        fontFamily: "Inter, sans-serif",
        fill: isSelected
          ? styleSettings.unitColor
          : m.color ?? styleSettings.angle.color,
        selectable: false,
        evented: false,
        hasControls: false,
        data: { type: "measurement-label", measurementId: m.id },
      });
      fabricCanvas.add(angleLabel);
    }
  }

  // --- Polygons
  const sortedPolygons = [...polygons].sort((a, b) => {
    // imported polygons (isImport) come first
    if (a.isImport && !b.isImport) return -1;
    if (!a.isImport && b.isImport) return 1;
    return 0;
  });
  for (const poly of sortedPolygons) {
    const isSelected = !poly.isImport && selectedPolygon === poly.id;
    const isHighlighted =
      _highlightedPolygonId && _highlightedPolygonId === poly.id;
    // pick the effective style: per-object > global setting
    const effectiveLineStyle =
      poly.lineStyle ?? styleSettings.polygon?.dashStyle ?? "solid";

    // map style -> dash array
    const dashFor = (style) =>
      style === "dashed" ? [5, 5] : style === "dotted" ? [2, 2] : undefined; // solid

    if (poly.points.length >= 3 && poly.isClosed) {
      const polygonFill = new fabric.Polygon(poly.points, {
        fill: isSelected
          ? "rgba(147, 51, 234, 0.1)"
          : poly.isImport
          ? styleSettings.importBgColor
          : isHighlighted
          ? styleSettings.highlightedColor
          : "rgba(147, 51, 234, 0.05)",

        stroke: isSelected
          ? styleSettings.unitColor
          : poly.isImport
          ? styleSettings.importStrokeColor
          : isHighlighted
          ? styleSettings.highlightedColor
          : poly.color ?? styleSettings.polygon.color,

        strokeWidth: poly.thickness ?? styleSettings.polygon.thickness,
        strokeDashArray: poly.isImport
          ? undefined
          : dashFor(effectiveLineStyle),
        selectable: false,
        evented: !poly.isImport && activeTool === "select", // 👈 block events for imported
        hasControls: false,
        data: { type: "polygon", id: poly.id },
      });
      fabricCanvas.add(polygonFill);
    } else if (poly.points.length > 1) {
      const polyline = new fabric.Polyline(poly.points, {
        fill: "transparent",
        stroke: isSelected
          ? styleSettings.unitColor
          : poly.isImport
          ? styleSettings.importStrokeColor
          : styleSettings.polygon.color,
        strokeWidth: isSelected
          ? poly.thickness ?? styleSettings.polygon.thickness
          : poly.isImport
          ? styleSettings.importStrokeColor
          : isHighlighted
          ? styleSettings.highlightedColor
          : styleSettings.polygon.thickness,
        selectable: false,
        evented: !poly.isImport && activeTool === "select",

        data: { type: "polygon", id: poly.id },
        hasControls: false,
      });
      fabricCanvas.add(polyline);
    }

    for (let i = 0; i < poly.points.length; i++) {
      const point = poly.points[i];
      const polygonPoint = new fabric.Circle({
        left: point.x - (isSelected ? 3 : 3),
        top: point.y - (isSelected ? 3 : 3),
        radius: isSelected ? 3 : poly.isImport ? 0 : 3,
        fill: isSelected
          ? styleSettings.unitColor
          : poly.isImport
          ? styleSettings.importCircleColor
          : isHighlighted
          ? styleSettings.highlightedColor
          : poly.color ?? styleSettings.polygon.color,
        selectable: false,
        evented: !poly.isImport && activeTool === "select",

        hasControls: false,
        data: {
          type: "polygon-point",
          polygonId: poly.id,
          pointIndex: i,
        },
      });
      fabricCanvas.add(polygonPoint);
    }

    if (poly.isClosed && poly.points.length >= 3) {
      const centerX =
        poly.points.reduce((s, p) => s + p.x, 0) / poly.points.length;
      const centerY =
        poly.points.reduce((s, p) => s + p.y, 0) / poly.points.length;
      // recompute using current scale/scaleLength so px↔mm updates immediately
      const freshArea = calculatePolygonArea(
        poly.points,
        scale,
        scaleLength,
        fabricCanvas // pass the same ref you already have in this file
      );

      // ✅ Preferred (uses your helper if it's exported)
      const freshPerimeter = calculatePolygonPerimeter(poly.points, (a, b) =>
        computeDistance(a, b, { scale, scaleLength })
      );

      const labelOffset = poly.isImport ? -20 : 0; // imported label moves slightly up

      // const areaLabel = new fabric.Text(
      //   `Area: ${freshArea.toFixed(2)} ${unit} ${unit === "px" ? "" : "²"} `,
      //   {
      //     left: centerX + 10,
      //     top: centerY - 10 + labelOffset,
      //     fontSize: 12,
      //     fontFamily: "Inter, sans-serif",
      //     fill: isSelected
      //       ? styleSettings.unitColor
      //       : poly.isImport
      //       ? styleSettings?.importStrokeColor
      //       : isHighlighted
      //       ? styleSettings.highlightedColor
      //       : poly.color ?? styleSettings.polygon.color,
      //     selectable: false,
      //     evented: false,
      //     data: { type: "polygon-area-label", polygonId: poly.id },
      //     hasControls: false,
      //   }
      // );
      // fabricCanvas.add(areaLabel);

      // const perimeterLabel = new fabric.Text(
      //   `Perimeter: ${freshPerimeter.toFixed(2)} ${unit}`,
      //   {
      //     left: centerX + 10,
      //     top: centerY + 10,
      //     fontSize: 12,
      //     fontFamily: "Inter, sans-serif",
      //     fill: isSelected
      //       ? styleSettings.unitColor
      //       : styleSettings.polygon.color,
      //     selectable: false,
      //     evented: false,
      //     data: { type: "polygon-perimeter-label", polygonId: poly.id },
      //     hasControls: false,
      //   }
      // );
      // fabricCanvas.add(perimeterLabel);
    }
  }

  // --- Guidelines
  // for (const g of guidelines) {
  //   const isSelected = selectedGuideline === g.id;
  //   let line;
  //   if (g.type === "vertical") {
  //     line = new fabric.Line([g.position, 0, g.position, canvasSize.height], {
  //       stroke: isSelected ? styleSettings.unitColor : "#8B5CF6",
  //       strokeWidth: isSelected ? 2 : 2,
  //       selectable: false,
  //       evented: activeTool === "select",
  //       data: { type: "guideline", id: g.id },
  //       hasControls: false,
  //     });
  //   } else {
  //     line = new fabric.Line([0, g.position, canvasSize.width, g.position], {
  //       stroke: isSelected ? styleSettings.unitColor : "#8B5CF6",
  //       strokeWidth: isSelected ? 2 : 2,
  //       selectable: false,
  //       evented: activeTool === "select",
  //       data: { type: "guideline", id: g.id },
  //       hasControls: false,
  //     });
  //   }
  //   fabricCanvas.add(line);
  // }
  // --- Guidelines (draw first; keep under everything)

  // --- Comments
  if (activeTool === "comment") {
    for (const c of comments) {
      const isSelected = selectedCommentId === c.id;
      const pinSize = isSelected ? 16 : 14;

      const pin = new fabric.Circle({
        left: c.position.x - pinSize,
        top: c.position.y - pinSize,
        radius: pinSize,
        fill: c.isResolved ? "#10b981" : "#3b82f6",
        stroke: "white",
        strokeWidth: 0,
        selectable: false,
        evented: activeTool === "select",
        data: { type: "comment", id: c.id },
      });
      fabricCanvas.add(pin);

      // Use a local PNG instead of the emoji text
      if (COMMENT_ICON_SRC) {
        fabric.Image.fromURL(COMMENT_ICON_SRC, { crossOrigin: "anonymous" })
          .then((img) => {
            img.set({
              originX: "center",
              originY: "center",
              selectable: false,
              evented: false,
              data: { type: "comment-icon", commentId: c.id },
            });

            const targetSize = Math.round(12 * 1.2);
            const iw = img.width || targetSize;
            const ih = img.height || targetSize;
            const scale = Math.min(targetSize / iw, targetSize / ih) || 1;

            img.set({
              left: c.position.x,
              top: c.position.y + 1,
              scaleX: scale,
              scaleY: scale,
            });

            fabricCanvas.add(img);
            // img.bringToFront();
            fabricCanvas.requestRenderAll();
          })
          .catch((err) => {
            console.error(
              "Failed to load comment icon:",
              COMMENT_ICON_SRC,
              err
            );
            // (Optional) fallback to emoji here
          });
      }

      if (isSelected) {
        const ring = new fabric.Circle({
          left: c.position.x - pinSize - 3,
          top: c.position.y - pinSize - 3,
          radius: pinSize,
          fill: "transparent",
          stroke: "#f59e0b",
          strokeWidth: 0,
          selectable: false,
          evented: false,
          data: { type: "comment-selection", commentId: c.id },
        });
        fabricCanvas.add(ring);
      }
    }
  }

  // --- Active drawings / previews
  if (currentPoints.length > 0) {
    if (activeTool === "polygon") {
      if (currentPoints.length > 1) {
        const currentPolyline = new fabric.Polyline(currentPoints, {
          fill: "transparent",
          stroke: "#FFD700",
          strokeWidth: 2,
          selectable: false,
          evented: false,
          data: { type: "current-drawing" },
        });
        fabricCanvas.add(currentPolyline);
      }

      if (hoveredPoint && currentPoints.length > 0) {
        const previewLine = new fabric.Line(
          [
            currentPoints[currentPoints.length - 1].x,
            currentPoints[currentPoints.length - 1].y,
            hoveredPoint.x,
            hoveredPoint.y,
          ],
          {
            stroke: "#FFD700",
            strokeWidth: 2,
            strokeDashArray: [2, 2],
            selectable: false,
            evented: false,
            data: { type: "preview-line" },
          }
        );
        fabricCanvas.add(previewLine);

        if (
          currentPoints.length >= 3 &&
          isPointNearPoint(hoveredPoint, currentPoints[0], 15)
        ) {
          const closingLine = new fabric.Line(
            [
              hoveredPoint.x,
              hoveredPoint.y,
              currentPoints[0].x,
              currentPoints[0].y,
            ],
            {
              stroke: "#FFD700",
              strokeWidth: 2,
              strokeDashArray: [2, 2],
              selectable: false,
              evented: false,
              data: { type: "closing-preview" },
            }
          );
          fabricCanvas.add(closingLine);
        }
      }

      for (let i = 0; i < currentPoints.length; i++) {
        const pt = currentPoints[i];
        const cp = new fabric.Circle({
          left: pt.x - (i === 0 ? 6 : 4),
          top: pt.y - (i === 0 ? 6 : 4),
          radius: i === 0 ? 6 : 4,
          fill: "#9333ea",
          selectable: false,
          evented: false,
          data: { type: "current-point" },
        });
        fabricCanvas.add(cp);
      }
    } else if (activeTool === "linear" && currentPoints.length === 1) {
      if (hoveredPoint) {
        const previewLine = new fabric.Line(
          [
            currentPoints[0].x,
            currentPoints[0].y,
            hoveredPoint.x,
            hoveredPoint.y,
          ],
          {
            stroke: "#3b82f6",
            strokeWidth: 2,
            strokeDashArray: [5, 5],
            selectable: false,
            evented: false,
            data: { type: "preview-line" },
          }
        );
        fabricCanvas.add(previewLine);
      }

      const startPoint = new fabric.Circle({
        left: currentPoints[0].x - 4,
        top: currentPoints[0].y - 4,
        radius: 4,
        fill: "#3b82f6",
        selectable: false,
        evented: false,
        data: { type: "current-point" },
      });
      fabricCanvas.add(startPoint);
    } else if (activeTool === "angle") {
      for (const pt of currentPoints) {
        const cp = new fabric.Circle({
          left: pt.x - 4,
          top: pt.y - 4,
          radius: 4,
          fill: "#ef4444",
          selectable: false,
          evented: false,
          data: { type: "current-point" },
        });
        fabricCanvas.add(cp);
      }

      if (currentPoints.length >= 2) {
        const A = currentPoints[0];
        const B = currentPoints[1]; // vertex
        const arm1 = new fabric.Line([B.x, B.y, A.x, A.y], {
          stroke: "#ef4444",
          strokeWidth: 2,
          selectable: false,
          evented: false,
          data: { type: "current-arm" },
        });
        fabricCanvas.add(arm1);
      }

      if (currentPoints.length === 2 && hoveredPoint) {
        const B = currentPoints[1]; // vertex
        const previewArm = new fabric.Line(
          [B.x, B.y, hoveredPoint.x, hoveredPoint.y],
          {
            stroke: "#ef4444",
            strokeWidth: 2,
            strokeDashArray: [5, 5],
            selectable: false,
            evented: false,
            data: { type: "preview-arm" },
          }
        );
        fabricCanvas.add(previewArm);
      }
    }
  }

  fabricCanvas.renderAll();
};
